import React, { useEffect, useState } from 'react';

function hhmm(v: any): string {
  if (v == null || v === '') return '';
  if (typeof v === 'number') {
    const h = Math.floor(v / 60), m = Math.round(v % 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }
  const s = String(v);
  const m = /^(\d{1,2}):(\d{2})/.exec(s);
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : '';
}

type Mode = 'any' | 'between' | 'exact';

function modeOf(value: any): Mode {
  if (!value) return 'any';
  if (value.any) return 'any';
  if (value.visit_exact || value.exact != null) return 'exact';
  if (value.visit_earliest || value.visit_latest || value.earliest != null || value.latest != null) return 'between';
  return 'any';
}

/**
 * Any time · Between … and … · Exactly …
 * Owner, Sep 22 2026: "if I ask someone hey can you do two to six then I can select that in the quote".
 */
export default function VisitHours({ value, onSave, saving }: {
  value?: any;
  onSave: (body: { hours_mode: Mode; visit_exact?: string; visit_earliest?: string; visit_latest?: string }) => void;
  saving?: boolean;
}) {
  const [mode, setMode] = useState<Mode>(() => modeOf(value));
  const [exact, setExact] = useState(() => hhmm(value?.visit_exact ?? value?.exact));
  const [earliest, setEarliest] = useState(() => hhmm(value?.visit_earliest ?? value?.earliest));
  const [latest, setLatest] = useState(() => hhmm(value?.visit_latest ?? value?.latest));

  useEffect(() => {
    setMode(modeOf(value));
    setExact(hhmm(value?.visit_exact ?? value?.exact));
    setEarliest(hhmm(value?.visit_earliest ?? value?.earliest));
    setLatest(hhmm(value?.visit_latest ?? value?.latest));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.visit_exact, value?.visit_earliest, value?.visit_latest, value?.exact, value?.earliest, value?.latest, value?.any]);

  function save(next: Mode = mode) {
    if (next === 'any') onSave({ hours_mode: 'any' });
    else if (next === 'exact') onSave({ hours_mode: 'exact', visit_exact: exact });
    else onSave({ hours_mode: 'between', visit_earliest: earliest, visit_latest: latest });
  }

  const tap = (next: Mode) => {
    setMode(next);
    if (next === 'any') onSave({ hours_mode: 'any' });
  };

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold text-gray-600">When they can do the visit</p>
      <div className="flex flex-wrap gap-1.5">
        {([
          ['any', 'Any time'],
          ['between', 'Between'],
          ['exact', 'Exactly'],
        ] as [Mode, string][]).map(([k, label]) => (
          <button key={k} type="button" onClick={() => tap(k)}
            className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${mode === k ? 'border-brand-500 bg-brand-600 text-black' : 'border-gray-300 text-gray-700'}`}>
            {label}
          </button>
        ))}
      </div>
      {mode === 'between' && (
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-xs text-gray-600">From
            <input type="time" className="input mt-1 w-32" value={earliest} onChange={e => setEarliest(e.target.value)} />
          </label>
          <label className="text-xs text-gray-600">To
            <input type="time" className="input mt-1 w-32" value={latest} onChange={e => setLatest(e.target.value)} />
          </label>
          <button type="button" onClick={() => save('between')} disabled={saving || (!earliest && !latest)}
            className="btn-secondary text-xs disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}
      {mode === 'exact' && (
        <div className="flex items-end gap-2">
          <label className="text-xs text-gray-600">They need
            <input type="time" className="input mt-1 w-32" value={exact} onChange={e => setExact(e.target.value)} />
          </label>
          <button type="button" onClick={() => save('exact')} disabled={saving || !exact}
            className="btn-secondary text-xs disabled:opacity-50">{saving ? 'Saving…' : 'Save'}</button>
        </div>
      )}
      {mode === 'any' && <p className="text-[11px] text-gray-500">Any slot we offer.</p>}
    </div>
  );
}
